/**
 * SOVRANOVA ErrorBoundary
 * Catches any unhandled React render errors — prevents blank screen crashes.
 * Must be a class component (React requirement for error boundaries).
 */
import React from 'react'

export class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props)
    this.state = { hasError: false, error: null, errorInfo: null }
  }

  static getDerivedStateFromError(error) {
    return { hasError: true, error }
  }

  componentDidCatch(error, errorInfo) {
    // Log to console for dev visibility — replace with error reporting service in prod
    console.error('SOVRANOVA ErrorBoundary caught:', error, errorInfo)
    this.setState({ errorInfo })
  }

  handleTryAgain = () => {
    this.setState({ hasError: false, error: null, errorInfo: null })
  }

  render() {
    if (this.state.hasError) {
      return (
        <div style={{
          minHeight: '100vh', display: 'flex', alignItems: 'center',
          justifyContent: 'center', padding: '1rem',
          background: 'linear-gradient(135deg, #e8f0f0 0%, #fdfcf9 60%)',
        }}>
          <div style={{
            width: '100%', maxWidth: '420px', background: 'white',
            borderRadius: '1.25rem', padding: '2.5rem 2rem',
            boxShadow: '0 4px 24px rgba(0,0,0,0.08)',
            display: 'flex', flexDirection: 'column', gap: '1.25rem',
            textAlign: 'center',
          }}>
            <div style={{ fontSize: '2rem', opacity: 0.15 }}>◎</div>
            <h2 style={{ fontSize: '1.2rem', fontWeight: 600, color: '#2c2c2c' }}>
              Something went wrong
            </h2>
            <p style={{ color: '#888', fontSize: '0.9rem', lineHeight: 1.6 }}>
              SOVRANOVA hit an unexpected error. Your session data and memory are safe —
              this is a display issue only.
            </p>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '0.625rem' }}>
              <button onClick={this.handleTryAgain} style={{
                padding: '0.8rem', background: '#7a9e9f', color: 'white',
                border: 'none', borderRadius: '0.75rem', fontSize: '0.975rem',
                fontWeight: 500, cursor: 'pointer',
              }}>
                Try Again
              </button>
              <button onClick={() => window.location.reload()} style={{
                padding: '0.8rem', background: 'transparent', color: '#888',
                border: '1.5px solid rgba(0,0,0,0.08)', borderRadius: '0.75rem',
                fontSize: '0.875rem', cursor: 'pointer',
              }}>
                Reload Page
              </button>
            </div>
            {process.env.NODE_ENV === 'development' && this.state.error && (
              <details style={{ textAlign: 'left', marginTop: '0.5rem' }}>
                <summary style={{ fontSize: '0.75rem', color: '#aaa', cursor: 'pointer' }}>
                  Error details (dev only)
                </summary>
                <pre style={{
                  fontSize: '0.7rem', color: '#c0392b', overflow: 'auto',
                  marginTop: '0.5rem', padding: '0.5rem',
                  background: '#fdf2f2', borderRadius: '0.5rem',
                }}>
                  {this.state.error?.toString()}
                </pre>
              </details>
            )}
          </div>
        </div>
      )
    }
    return this.props.children
  }
}
